'use client';

import { useRef, useEffect, type RefObject, useCallback } from 'react';

const SCROLL_THRESHOLD = 160;

export const useChatScroll = (dep: any): RefObject<HTMLDivElement | null> => {
  const ref = useRef<HTMLDivElement>(null);
  const previousMessageCountRef = useRef<number>(0);
  const userHasScrolledUpRef = useRef<boolean>(false);
  const isInitialLoadRef = useRef<boolean>(true);

  const handleScroll = useCallback(() => {
    if (!ref.current) return;

    const container = ref.current;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

    userHasScrolledUpRef.current = distanceFromBottom > SCROLL_THRESHOLD;
  }, []);

  useEffect(() => {
    const container = ref.current;

    if (!container) return;

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    if (!ref.current) return;

    const container = ref.current;
    const currentMessageCount = dep?.messages?.length || 0;

    const clientHeight = container.clientHeight;
    const distanceFromBottom =
      container.scrollHeight - (container.scrollTop + clientHeight);

    const hasNewMessages: boolean =
      currentMessageCount > previousMessageCountRef.current;
    const isAtBottom: boolean = distanceFromBottom <= SCROLL_THRESHOLD;

    // Auto-scroll conditions:
    // 1. Initial load
    // 2. New messages were added AND (user is at bottom OR user hasn't scrolled up)
    // This ensures if you're at/near bottom when sending, it scrolls down
    const shouldAutoScroll =
      isInitialLoadRef.current ||
      (hasNewMessages && (isAtBottom || !userHasScrolledUpRef.current));

    if (shouldAutoScroll) {
      container.scrollTop = container.scrollHeight;

      // After auto-scrolling, reset the userHasScrolledUp flag
      // so we continue to auto-scroll for subsequent messages
      userHasScrolledUpRef.current = false;
    }

    previousMessageCountRef.current = currentMessageCount;
    isInitialLoadRef.current = false;
  }, [dep]);

  return ref;
};
